// 3rd party imports goes here
import React, { useEffect } from "react";
import { Switch, Route, useHistory } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { hotjar } from "react-hotjar";

// Application imports goes here
import Design from "modules/Design/Shared/Design";
import Login from "./modules/Login/Access/Components/Login";
import Content from "./modules/Content/Shared/ContentTab";
import LaunchIntro from "./modules/Login/Welcome/Components/LaunchIntro";
import PrivateRoute from "./modules/Login/Access/Components/PrivateRoute";
import Overlay from "./modules/Common/Components/Overlay";
import SignUpPage from "./modules/Login/Access/Components/SignUpPage";
import ResetPassword from "modules/Login/Access/Components/ResetPassword";
import DigestWithSearch from "./modules/Campaigns/CampaignContent/Components/DigestWithSearch";
import LoginRedirect from "modules/Login/Access/Components/LoginRedirect";
import Publish from "modules/Publish/Components/Publish";
import CreateCampaign from "modules/Campaigns/CampaignContent/Components/CreateCampaign";
import Audience from "modules/Campaigns/CampaignContent/Components/Audience";
import WelcomeAnimation from "./modules/Common/Components/WelcomeAnimation";
import AbandonPopup from "modules/Content/DragnDrop/AbandonPopup";
import ContactsImport from "modules/Login/Welcome/Components/ContactsImport";
import SSO from "modules/Login/Access/Components/SSO";
import EmailWithOutlookRedirect from "modules/Publish/Components/EmailWithOutlookRedirect";
import PermissionDenied from "./modules/Campaigns/Shared/PermissionDenied";
import { resetErrorAction, setErrorPageAction, setErrorCodeAction } from "modules/Error/actions";
import useQuery from "../src/hooks/useQuery";
import { Freshchat } from "reactjs-freshchat";

// css files goes here
import "./assets/scss/custom-antd.css";
import "./App.css";
import "./assets/scss/index.css";
import { removeGillRoyIcon, addGillRoyIcon } from "utils";
import { hideFreshDeskUrl } from "constants/index";
import PreferenceManagement from "modules/Preference/PreferenceManagement";

const App: React.FC = () => {
  const isOverlay = useSelector((state: RootState) => state.uiReducer.overlay);
  const errorType: string = useSelector((state: RootState) => state.errors.errorType);
  const isSelectionedEmail = useSelector((state: RootState) => state.dragDrop.gillRoyMoveUp);
  const exportWidgetPermission = useSelector(
    (state: RootState) => state?.user?.userData?.export_widget_permission
  );
  const history = useHistory();
  let query = useQuery();

  useEffect(() => {
    if (errorType !== "") {
      const fromQuery = query.get("from");
      const fromQueryWorkflowId = query.get("workflow_id");
      let path = window.location.pathname;
      let fromAutomation = path.startsWith("/automation");
      if (fromQuery === "campaigns") {
        resetErrorAction();
        setErrorCodeAction(parseInt(errorType));
        setErrorPageAction(`/campaigns/content?campaign_id=${query.get("campaign_id")}`, true);
      } else if (fromAutomation) {
        resetErrorAction();
        setErrorCodeAction(parseInt(errorType));
        setErrorPageAction(`/automation`, true);
      } else if (fromQueryWorkflowId) {
        resetErrorAction();
        setErrorCodeAction(parseInt(errorType));
        setErrorPageAction(`/automation/details?workflow_id=${fromQueryWorkflowId}`, true);
      } else if (!exportWidgetPermission && path.includes("groups")) {
        resetErrorAction();
        setErrorCodeAction(parseInt(errorType));
        setErrorPageAction(`/campaigns`, true);
      }
      history.push("/permission_denied?error=" + errorType);
    }
  }, [errorType]);

  useEffect(() => {
    if (isOverlay) removeGillRoyIcon();
    else if (!hideFreshDeskUrl.includes(window.location.pathname) && !isSelectionedEmail) {
      addGillRoyIcon();
    }
  }, [isOverlay]);

  useEffect(() => {
    let path = window.location.pathname;
    if (
      (path.startsWith("/campaigns") && hideFreshDeskUrl.includes(path)) ||
      path.startsWith("/automation") ||
      path.startsWith("/welcome") ||
      path.startsWith("/unsub")
    ) {
      removeGillRoyIcon();
    }
  }, [window.location.pathname]);

  useEffect(() => {
    hotjar.initialize(3221786, 6);
  }, []);

  return (
    <div>
      <DndProvider backend={HTML5Backend}>
        <AbandonPopup />
        {isOverlay && <Overlay />}
        <Switch>
          <Route exact path="/" component={Login} />
          <Route path="/resetPassword/:resetKey?" component={ResetPassword} />
          <Route path="/login-redirect" component={LoginRedirect} />
          <Route exact path="/WelcomeAnimation" component={WelcomeAnimation} />
          <Route exact path="/sso/:csrfToken/" component={SSO} />
          <Route path="/unsub-success" component={PreferenceManagement} exact />
          <Route path="/unsub/:unsubid" component={PreferenceManagement} exact />

          <PrivateRoute path="/permission_denied" component={PermissionDenied} exact />
          <PrivateRoute path="/import" component={ContactsImport} exact />
          <PrivateRoute path="/welcome/intro" component={LaunchIntro} exact />
          <PrivateRoute path="/welcome" component={WelcomeAnimation} exact />
          <PrivateRoute path="/content/:tab" component={Content} exact />
          <PrivateRoute path="/design" component={Design} exact />
          <PrivateRoute path="/publish" component={Publish} exact />
          <PrivateRoute path="/signup" component={SignUpPage} exact />
          <PrivateRoute path="/campaigns/:page?/:tab?/:subTab?" component={Content} exact />
          <PrivateRoute path="/digestSearch" component={DigestWithSearch} exact />
          <PrivateRoute path="/createcampaign" component={CreateCampaign} exact />
          <PrivateRoute path="/audiences/:page?/:tab?" component={Content} exact />
          <PrivateRoute path="/campaignAudience" component={Audience} exact />
          <PrivateRoute path="/analytics/:page?/:tab?" component={Content} exact />
          <PrivateRoute path="/analytics/:page?" component={Content} exact />
          <PrivateRoute path="/outlook_auth" component={EmailWithOutlookRedirect} exact />
          <PrivateRoute path="/automation/:page?/:tab?" component={Content} exact />
          {/** Uncomment the following to include Custom Widget Groups */}
          <PrivateRoute path="/groups/:page?" component={Content} exact />
        </Switch>
      </DndProvider>
      <Freshchat
        token={"1f9215d1-96af-4a4d-9634-cecb117ff337"}
        host={"https://wchat.freshchat.com"}
        externalId={localStorage.getItem("email") || ""}
        email={localStorage.getItem("email") || ""}
        firstName={localStorage.getItem("fullname") || ""}
        faqTags={{
          tags: ["Workbench"],
          filterType: "category",
        }}
      />
    </div>
  );
};

export default App;
